<template>
  <div class="event-players-list" v-if="loaded">
    <div class="players-summary">
      <TjingToolTip text="Max spots" :hideOnDesktop="true">
        <SpotsLeft color="grey" />
        <p>
          <strong>{{ eventData.maxSpots }}</strong>
          <span> Max {{ eventData.maxSpots == 1 ? 'spot' : 'spots' }}</span>
        </p>
      </TjingToolTip>
      <TjingToolTip text="Available spots" :hideOnDesktop="true">
        <SpotsLeft />
        <p>
          <strong>{{ eventData.maxSpots - eventData.playersCount }}</strong>
          <span>
            Available
            {{
              eventData.maxSpots - eventData.playersCount == 1
                ? 'spot'
                : 'spots'
            }}</span
          >
        </p>
      </TjingToolTip>
      <TjingToolTip text="Confirmed players" :hideOnDesktop="true">
        <ConfirmedPlayers />
        <p>
          <strong>{{ eventData.playersCount }}</strong>
          <span>
            Confirmed
            {{ eventData.playersCount == 1 ? 'player' : 'players' }}</span
          >
        </p>
      </TjingToolTip>
      <TjingToolTip text="Waiting players" :hideOnDesktop="true">
        <WaitingList />
        <p>
          <strong>{{ eventData.waitingListLength }}</strong
          ><span>
            Waiting
            {{ eventData.waitingListLength == 1 ? 'player' : 'players' }}</span
          >
        </p>
      </TjingToolTip>
    </div>
    <SmartStickyHeader>
      <div class="mobile-nav">
        <select
          v-model="selectedDivisionId"
          @change="updateRoute()"
          class="division-selector"
        >
          <option :value="null">All players</option>
          <option :value="division.id" v-for="division in eventData.divisions">
            {{ division.name }}
          </option>
        </select>
        <TextSwitch
          class="view-switch"
          :leftText="'Confirmed'"
          :rightText="'Waiting'"
          :startLeft="$route.params.view === 'confirmed'"
          @change="changeView($event)"
        />
      </div>
    </SmartStickyHeader>
    <div class="desktop-nav">
      <CustomDropDown
        position="left"
        :items="divisionItems"
        :disabled="divisionItems.length === 1"
        @changeDivision="
          selectedDivisionId = $event
          updateRoute()
        "
      >
        <div class="selector-wrapper">
          <p>
            <b>{{ selectedDivisionName }}</b>
          </p>
          <ChevronFat direction="down" v-if="divisionItems.length > 1" />
        </div>
      </CustomDropDown>
      <TextSwitch
        class="view-switch"
        :leftText="'Confirmed'"
        :rightText="'Waiting'"
        :startLeft="$route.params.view === 'confirmed'"
        @change="changeView($event)"
      />
    </div>

    <div
      class="player-table"
      v-if="registrations.length > 0"
      :class="{
        combined: combinedWaitinglist === true || selectedDivisionId === null,
        teamname: showTeamName && isSingles === false,
      }"
    >
      <div class="table-header">
        <p class="position"></p>
        <div
          class="player"
          :class="{
            show: shownColumn === 'firstName',
            active: orderBy === 'firstName',
          }"
          @click="changeSort('firstName')"
        >
          <p>{{ isSingles ? 'Player' : 'Team' }}</p>
          <DirectionArrow
            :direction="direction === 'ASC' ? 'up' : 'down'"
            v-if="orderBy === 'firstName'"
          />
        </div>
        <p class="division">Division</p>
        <p class="club">Club</p>
        <div
          class="pdga"
          @click="changeSort('pdgaNumber')"
          :class="{
            show: shownColumn === 'pdgaNumber',
            active: orderBy === 'pdgaNumber',
          }"
        >
          <DirectionArrow
            class="desktop"
            :direction="direction === 'ASC' ? 'up' : 'down'"
            v-if="orderBy === 'pdgaNumber'"
          />
          <p>PDGA</p>
          <DirectionArrow
            class="mobile"
            :direction="direction === 'ASC' ? 'up' : 'down'"
            v-if="orderBy === 'pdgaNumber'"
          />
        </div>

        <div
          class="rating"
          @click="changeSort('pdgaRating')"
          :class="{
            show: shownColumn === 'pdgaRating',
            active: orderBy === 'pdgaRating',
          }"
        >
          <DirectionArrow
            class="desktop"
            :direction="direction === 'ASC' ? 'up' : 'down'"
            v-if="orderBy === 'pdgaRating'"
          />
          <p>Rating</p>
          <DirectionArrow
            class="mobile"
            :direction="direction === 'ASC' ? 'up' : 'down'"
            v-if="orderBy === 'pdgaRating'"
          />
        </div>
        <div
          @click="changeSort('createdAt')"
          class="registered"
          :class="{
            show: shownColumn === 'createdAt',
            active: orderBy === 'createdAt',
          }"
        >
          <DirectionArrow
            class="desktop"
            :direction="direction === 'ASC' ? 'up' : 'down'"
            v-if="orderBy === 'createdAt'"
          />
          <p></p>
          <DirectionArrow
            class="mobile"
            :direction="direction === 'ASC' ? 'up' : 'down'"
            v-if="orderBy === 'createdAt'"
          />
        </div>
        <div class="nav-arrows" v-if="!showTeamName">
          <div
            class="previous"
            :class="{ active: columnIndex > 0 }"
            @click="increaseColumnIndex()"
          >
            <Chevron2
              direction="left"
              :color="columnIndex > 0 ? 'midnight' : 'sleet'"
            />
          </div>
          <div
            class="next"
            :class="{ active: columnIndex < availableColumns.length - 1 }"
            @click="decreaseColumnIndex()"
          >
            <Chevron2
              direction="right"
              :color="
                columnIndex < availableColumns.length - 1 ? 'midnight' : 'sleet'
              "
            />
          </div>
        </div>
      </div>
      <div class="table-body" v-hammer:swipe="(event) => moveDivision(event)">
        <div
          class="player-row"
          :class="{
            doubles: isSingles === false,
          }"
          v-for="(registration, regIndex) in registrations"
        >
          <p class="position">
            {{ pagination.perPage * pagination.page + regIndex + 1 }}
          </p>
          <div
            class="player"
            :class="{
              active: orderBy === 'firstName',
            }"
          >
            <template v-if="isSingles">
              <ProfilePopoverCard :userId="registration.user.id">
                <div class="player-wrapper">
                  <UserProfileAvatar
                    class="left"
                    size="S"
                    desktopSize="D-M"
                    :initials="
                      registration.user.firstName.charAt(0) +
                      registration.user.lastName.charAt(0)
                    "
                    :profileImageUrl="registration.user.profile.profileImageUrl"
                  />
                  <p>
                    {{ registration.user.firstName }}
                    {{ registration.user.lastName }}
                  </p>
                </div>
              </ProfilePopoverCard>
            </template>
            <template v-else>
              <div v-if="showTeamName" class="team">
                <p @click="viewedTeamIndex = regIndex">
                  {{ registration.team.name }}
                </p>

                <TjingClickToolTip
                  :key="registration.team.id + 'pending'"
                  iconType="warning"
                  v-if="hasPendingMember(registration.team.teamPlayers)"
                >
                  <p class="please-note">
                    All players hasn't yet accepted the team invitation.
                  </p>
                </TjingClickToolTip>
              </div>
              <div
                clasS="teamplayer-wrapper"
                v-else
                v-for="teamPlayer in registration.team.teamPlayers"
              >
                <ProfilePopoverCard
                  :userId="teamPlayer.userId"
                  :key="teamPlayer.user.id"
                >
                  <div class="player-wrapper">
                    <UserProfileAvatar
                      class="left"
                      size="S"
                      desktopSize="D-M"
                      :initials="
                        teamPlayer.user.firstName.charAt(0) +
                        teamPlayer.user.lastName.charAt(0)
                      "
                      :profileImageUrl="teamPlayer.user.profile.profileImageUrl"
                    />
                    <p>
                      {{ teamPlayer.user.firstName }}
                      {{ teamPlayer.user.lastName }}
                    </p>
                  </div>
                </ProfilePopoverCard>
                <TjingClickToolTip
                  :key="teamPlayer.user.id + 'pending'"
                  iconType="warning"
                  v-if="teamPlayer.status === 'PENDING'"
                >
                  <p class="please-note">
                    This player hasn't yet accepted the team invitation.
                  </p>
                </TjingClickToolTip>
              </div>
            </template>
          </div>
          <div class="division">
            <p>{{ registration.division.type }}</p>
          </div>
          <div class="club" v-if="isSingles">
            <router-link
              v-if="registration.user.profile.mainClubSlug"
              :to="{
                name: 'club',
                params: { slug: registration.user.profile.mainClubSlug },
              }"
            >
              <p>
                {{ registration.user.profile.mainClubName }}
              </p>
            </router-link>
            <p v-else>
              {{ registration.user.profile.club || '-' }}
            </p>
          </div>
          <div class="club" v-else>
            <template v-for="teamPlayer in registration.team.teamPlayers">
              <router-link
                v-if="teamPlayer.user.profile.mainClubSlug"
                :key="teamPlayer.user.id"
                :to="{
                  name: 'club',
                  params: { slug: teamPlayer.user.profile.mainClubSlug },
                }"
              >
                <p>
                  {{ teamPlayer.user.profile.mainClubName }}
                </p>
              </router-link>
              <p v-else>
                {{ teamPlayer.user.profile.club || '-' }}
              </p>
            </template>
          </div>
          <div
            class="pdga"
            :class="{
              show: shownColumn === 'pdgaNumber',
              active: orderBy === 'pdgaNumber',
            }"
          >
            <template v-if="isSingles">
              <a
                v-if="registration.user.profile.pdgaNumber"
                :href="
                  'https://www.pdga.com/player/' +
                  registration.user.profile.pdgaNumber
                "
                target="_blank"
                >{{ registration.user.profile.pdgaNumber }}</a
              >
              <template v-else>
                <p>-</p>
              </template>
            </template>
            <template v-else>
              <template v-for="teamPlayer in registration.team.teamPlayers">
                <a
                  v-if="teamPlayer.user.profile.pdgaNumber"
                  :href="
                    'https://www.pdga.com/player/' +
                    teamPlayer.user.profile.pdgaNumber
                  "
                  target="_blank"
                  >{{ teamPlayer.user.profile.pdgaNumber }}</a
                >
                <template v-else>
                  <p>-</p>
                </template>
              </template>
            </template>
          </div>
          <div
            class="rating"
            :class="{
              show: shownColumn === 'pdgaRating',
              active: orderBy === 'pdgaRating',
            }"
          >
            <template v-if="isSingles">
              <a
                v-if="registration.user.profile.pdgaRating"
                :href="
                  'https://www.pdga.com/player/' +
                  registration.user.profile.pdgaNumber
                "
                target="_blank"
                >{{ registration.user.profile.pdgaRating }}</a
              >
              <template v-else>
                <p>-</p>
              </template>
            </template>
            <template v-else>
              <template v-for="teamPlayer in registration.team.teamPlayers">
                <a
                  v-if="teamPlayer.user.profile.pdgaRating"
                  :href="
                    'https://www.pdga.com/player/' +
                    teamPlayer.user.profile.pdgaNumber
                  "
                  target="_blank"
                  >{{ teamPlayer.user.profile.pdgaRating }}</a
                >
                <template v-else>
                  <p>-</p>
                </template>
              </template>
            </template>
          </div>
          <p
            class="registered mobile"
            :class="{
              show: shownColumn === 'createdAt',
              active: orderBy === 'createdAt',
            }"
          >
            {{ dateFormatter(registration.createdAt, 'D MMM') }}
          </p>
          <p
            class="registered desktop"
            :class="{
              show: shownColumn === 'createdAt',
              active: orderBy === 'createdAt',
            }"
          >
            {{ dateFormatter(registration.createdAt, 'D MMM, HH:mm') }}
          </p>
          <div class="nav-arrows">
            <span class="previous"></span>
            <span class="next"></span>
          </div>
        </div>
      </div>
    </div>
    <div class="placeholder-message" v-else>
      <PublicPagesPlaceholder
        v-hammer:swipe="(event) => moveDivision(event)"
        :title="
          $route.params.view === 'confirmed'
            ? 'No confirmed players'
            : 'No waiting players'
        "
        :text="
          $route.params.view === 'confirmed'
            ? $route.params.divisionId
              ? 'There are no confirmed players for this division.'
              : 'There are no confirmed players.'
            : $route.params.divisionId
            ? 'There are no waiting players for this division.'
            : 'There are no waiting players.'
        "
      />
    </div>

    <div class="pagination-wrapper">
      <div
        v-if="
          $route.params.view === 'waitinglist' &&
          eventData.waitinglistType.type === 'DIVISION' &&
          !$route.query.divisionId &&
          registrationsCount > 0
        "
        class="waitinglist-rules"
      >
        <TjingClickToolTip>
          <p>
            Spots are confirmed by division. To see when a player is next in
            line go to the specific division.
          </p>
          <p class="small-note">
            <b>Note: </b>Spots are given based on the event organisers’ terms.
          </p>
        </TjingClickToolTip>
        <p>Spot allocation</p>
      </div>
      <Pagination
        v-if="registrationsCount > pagination.perPage"
        @back="pagination.page--"
        @forward="pagination.page++"
        @start="pagination.page = 0"
        @end="
          pagination.page =
            Math.ceil(registrationsCount / pagination.perPage) - 1
        "
        :total="registrationsCount"
        :perPage="pagination.perPage"
        :currentPage="pagination.page"
      />
    </div>
    <div class="quick-nav-info-container">
      <SwipeNavigation />
      <p>
        Swiftly browse divisions by swiping left or right on the above table
      </p>
    </div>
    <TeamDetailsSidebar
      @close="viewedTeamIndex = null"
      :teams="leaderboardTeams"
      :startIndex="viewedTeamIndex"
      :show="viewedTeamIndex !== null"
    />
  </div>
</template>

<script>
import SmartStickyHeader from '@/components/SmartStickyHeader.vue'
import SwipeNavigation from '@/assets/icons/SwipeNavigation'
import TjingClickToolTip from '@/components/UIElements/TjingClickToolTip'
import Pagination from '@/components/Pagination.vue'
import ChevronFat from '@/assets/icons/ChevronFat'
import CustomDropDown from '@/components/CustomDropDown.vue'
import TextSwitch from '@/components/TextSwitch.vue'
import PublicPagesPlaceholder from '@/pages/manager/players/PublicPagesPlaceholder.vue'
import Chevron2 from '@/assets/icons/Chevron2'
import DirectionArrow from '@/assets/icons/DirectionArrow'
import ProfilePopoverCard from '@/components/ProfilePopoverCard'
import UserProfileAvatar from '@/components/UIElements/UserProfileAvatar'
import dateFormats from '@/mixins/dateFormats.js'
import TeamDetailsSidebar from '@/pages/TeamDetailsSidebar.vue'
import ConfirmedPlayers from '@/assets/icons/ConfirmedPlayers'
import WaitingList from '@/assets/icons/WaitingList'
import SpotsLeft from '@/assets/icons/SpotsLeft'
export default {
  name: 'PublicPlayers',
  components: {
    SmartStickyHeader,
    SwipeNavigation,
    TjingClickToolTip,
    Pagination,
    CustomDropDown,
    ChevronFat,
    TextSwitch,
    PublicPagesPlaceholder,
    Chevron2,
    DirectionArrow,
    ProfilePopoverCard,
    UserProfileAvatar,
    TeamDetailsSidebar,
    ConfirmedPlayers,
    WaitingList,
    SpotsLeft,
  },
  mixins: [dateFormats],
  props: ['isSingles'],
  data: function () {
    return {
      eventData: null,
      registrations: [],
      loaded: false,
      selectedDivisionId: null,
      sortDirection: 'ASC',
      registrationStatus: 'ACCEPTED',
      orderBy: 'createdAt',
      direction: 'ASC',
      columnIndex: 0,
      availableColumns: ['createdAt', 'pdgaNumber', 'pdgaRating'],
      pagination: {
        page: 0,
        perPage: 100,
      },
      viewedTeamIndex: null,
    }
  },
  watch: {
    direction: {
      handler: function (newVal, oldVal) {
        this.loadData()
      },
      deep: true,
    },
    orderBy: {
      handler: function (newVal, oldVal) {
        this.loadData()
      },
      deep: true,
    },
    $route: {
      handler: function (newVal, oldVal) {
        this.loadData()
      },
      deep: true,
    },
    pagination: {
      handler: function (newVal, oldVal) {
        this.loadData()
      },
      deep: true,
    },
  },
  computed: {
    leaderboardTeams() {
      return this.registrations.map((player) => player.team)
    },
    showTeamName() {
      if (this.eventData.singles === true) {
        return false
      }
      return this.eventData.defaultToTeamName || this.eventData.teamSize !== 2
    },
    combinedWaitinglist() {
      return (
        this.eventData.waitinglistType.type === 'COMBINED' &&
        this.registrationStatus === 'PENDING'
      )
    },
    shownColumn() {
      return this.availableColumns[this.columnIndex]
    },
    registrationsCount() {
      if (this.selectedDivisionId === null) {
        return this.registrationStatus === 'ACCEPTED'
          ? this.eventData.registrationsCountPerDivision.totalAccepted
          : this.eventData.registrationsCountPerDivision.totalWaiting
      }
      const count =
        this.eventData.registrationsCountPerDivision.countPerDivision.find(
          (division) => division.divisionId === this.selectedDivisionId
        )

      if (!count) return 0

      return this.registrationStatus === 'ACCEPTED'
        ? count.acceptedCount
        : count.waitingCount
    },
    selectedDivisionName() {
      const division = this.eventData.divisions.find(
        (division) => division.id === this.selectedDivisionId
      )

      return division ? division.name : 'All players'
    },
    divisionItems() {
      const items = [
        {
          title: 'All players',
          action: 'changeDivision',
          value: null,
          selected: this.selectedDivisionId === null,
        },
      ]

      items.push(
        ...this.eventData.divisions.map((division) => {
          return {
            title: division.name,
            action: 'changeDivision',
            value: division.id,
            selected: this.selectedDivisionId === division.id,
          }
        })
      )
      return items
    },
  },
  methods: {
    hasPendingMember(teamPlayers) {
      return teamPlayers.some((teamPlayer) => teamPlayer.status === 'PENDING')
    },
    changeSort(orderBy) {
      if (this.orderBy === orderBy) {
        this.direction = this.direction === 'ASC' ? 'DESC' : 'ASC'
      } else {
        this.orderBy = orderBy
        this.direction = 'ASC'
      }
    },
    decreaseColumnIndex() {
      if (this.columnIndex < this.availableColumns.length - 1) {
        this.columnIndex++
      }
    },
    increaseColumnIndex() {
      if (this.columnIndex > 0) {
        this.columnIndex--
      }
    },
    changeView(e) {
      this.registrationStatus = e ? 'ACCEPTED' : 'PENDING'
      this.updateRoute()
    },
    moveDivision(e) {
      if (
        this.eventData.divisions.length > 1 &&
        this.$store.state.utility.deviceWidth < 1200
      ) {
        const divisions = [
          ...this.eventData.divisions,
          { id: null, name: 'Leaders' },
        ]
        const currentIndex = divisions.findIndex(
          (division) => division.id === this.selectedDivisionId
        )
        if (e.direction === 2) {
          // If swiping right and currently at the last division, go to the first division
          this.selectedDivisionId =
            divisions[(currentIndex + 1) % divisions.length].id
        } else if (e.direction === 4) {
          // If swiping left and currently at the first division, go to the last division
          this.selectedDivisionId =
            divisions[
              (currentIndex - 1 + divisions.length) % divisions.length
            ].id
        }

        this.updateRoute()
      }
    },
    updateRoute() {
      this.pagination.page = 0
      this.$router.push({
        name: this.$route.name,
        query: {
          divisionId: this.selectedDivisionId ?? undefined,
        },
        params: {
          eventId: this.$route.params.eventId,
          view:
            this.registrationStatus === 'ACCEPTED'
              ? 'confirmed'
              : 'waitinglist',
        },
      })
    },
    async loadData() {
      const divisionQuery = this.selectedDivisionId
        ? `divisionId: "${this.selectedDivisionId}",`
        : ''

      const singlesQuery = `eventRegistrations(
                eventId: "${this.$router.currentRoute.params.eventId}",
                ${divisionQuery}
                status:  ${this.registrationStatus},
                first: ${this.pagination.perPage},
                skip:   ${this.pagination.page * this.pagination.perPage},
                orderBy: ${this.orderBy},
                direction: ${this.direction}
            ){
                id
                status
                createdAt
                statusUpdatedAt
                hasPaid
                message
                division {
                  id
                  type
                  isCustomDivision
                  position
                  name
                }
                user {
                  id
                  firstName
                  lastName
                  isAnonymous
                  email
                  isDisabled
                  profile {
                    club
                    mainClubName
                    mainClubSlug
                    mainClubChangedAt
                    pdgaNumber
                    pdgaRating
                    profileImageUrl
                  }
                }
              }`
      const teamsQuery = `eventTeamRegistrations(
                eventId: "${this.$router.currentRoute.params.eventId}",
                ${divisionQuery}
                status:  ${this.registrationStatus},
                first: ${this.pagination.perPage},
                skip:   ${this.pagination.page * this.pagination.perPage},
                orderBy: ${this.orderBy},
                direction: ${this.direction}
            ){
                id
                status
                createdAt
                statusUpdatedAt
                hasPaid
                message
                division {
                  id
                  type
                  isCustomDivision
                  position
                  name
                }
                team {
                  id
                  name
                  type
                  archived
                  teamPlayers {
                    id
                    userId
                    teamId
                    acceptedAt
                    status
                    user {
                      id
                      firstName
                      lastName
                      profile {
                        mainClubName
                        mainClubSlug
                        club
                        pdgaNumber
                        pdgaRating
                        profileImageUrl
                      }
                    }
                  }
                }
              }`
      const eventData = await this.$axios({
        headers: { Authorization: this.$store.state.user.sessionInfo.token },
        data: {
          query: `
          {
            ${this.isSingles ? singlesQuery : teamsQuery}
            event(eventId:"${this.$router.currentRoute.params.eventId}"){
              singles
              maxSpots
              playersCount
              waitingListLength
              defaultToTeamName
              teamSize
              registrationsCountPerDivision{
                totalAccepted
                totalWaiting
                totalCancelled
                countPerDivision{
                  divisionId
                  totalCount
                  acceptedCount
                  waitingCount
                  cancelledCount
                }
              }
              divisions{
                id
                name
              }
              waitinglistType{
                name
                type
              }
            }
          }
          `,
        },
      })
      this.eventData = eventData.data.data.event
      this.isSingles
        ? (this.registrations = eventData.data.data.eventRegistrations)
        : (this.registrations = eventData.data.data.eventTeamRegistrations)

      this.loaded = true
    },
  },
  beforeMount() {
    this.registrationStatus =
      this.$route.params.view === 'confirmed' ? 'ACCEPTED' : 'PENDING'
    this.selectedDivisionId = this.$route.query.divisionId ?? null
    this.loadData()
  },
}
</script>

<style scoped lang="scss">
@import '@/styles/Typography';

.quick-nav-info-container {
  display: flex;
  align-items: center;
  margin: 0 12px;
  padding: 10px 12px;
  background: $mist;
  border-radius: 4px;
  gap: 12px;

  p {
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  svg {
    min-width: 24px;
  }
}

.quick-nav-info-container {
  margin-top: 16px;
}

.waitinglist-rules {
  margin-right: auto;
  align-items: center;
  display: flex;
  width: 100%;

  .tooltip-container {
    margin-right: 8px;

    .small-note {
      line-height: 1.125rem;
      margin-top: 8px;
      font-size: 12px;
    }
  }
}

.teamplayer-wrapper {
  display: flex;
  gap: 8px;
  align-items: center;

  > div {
    overflow: hidden;
  }
}

.players-summary {
  border: 1px solid $fog;
  border-radius: 8px;
  padding: 11px 16px;
  margin: 0 12px 24px 12px;
  display: flex;
  justify-content: space-between;

  p {
    font-size: 16px;
  }

  strong {
    @include Gilroy-Bold;
  }

  svg {
    width: 16px;
    height: 16px;
  }

  > div {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  span {
    display: none;
  }
}

.team {
  display: flex;
  gap: 8px;
  align-items: center;
  cursor: pointer;
}

.view-switch {
  width: 100%;
}

.player-table {
  padding: 0 12px;

  p {
    font-size: 1rem;
  }

  &.combined {
    .player-row {
      grid-template-columns: 40px auto 60px 80px;

      .division {
        display: block;
        text-align: right;
      }
    }
  }

  &.teamname {
    .table-header {
      .position {
        display: none;
      }

      padding-left: 12px;
      grid-template-columns: auto;
    }
  }
}

.table-header {
  margin-bottom: 8px;
  display: grid;
  padding: 0 0 0 12px;
  grid-template-columns: auto auto;
  background: $mist;
  height: 40px;
  border-radius: 8px;

  > p,
  > div {
    display: none;
    font-size: 16px;
    color: $strom;
    @include Gilroy-SemiBold;
    align-self: center;

    &.show {
      display: flex;
      align-items: center;
    }

    &.active {
      .desktop {
        display: none;
      }

      p {
        color: $midnight;
      }

      svg {
        display: block;
      }
    }

    p {
      transition: all ease 0.3s;
      font-size: 16px;
      color: $strom;
      @include Gilroy-SemiBold;
    }

    > svg {
      margin-left: 8px;
      display: none;
    }
  }

  .registered {
    p::after {
      content: 'Registration date';
    }
  }

  .nav-arrows {
    justify-self: flex-end;
    display: grid;
    grid-template-columns: 40px 40px;
    align-items: stretch;
    justify-items: stretch;

    > div {
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all ease 0.3s;
      height: 40px;
    }

    .next {
      border-radius: 0 8px 8px 0;
    }

    .previous {
      border-radius: 8px 0 0 8px;
    }

    .active {
      transition: all ease 0.3s;
      background-color: $fog;
    }
  }
}

.player-row {
  display: grid;
  grid-template-columns: 30px auto 80px;
  align-items: center;
  height: 48px;
  border-bottom: 1px solid $fog;

  > div,
  > p {
    display: none;
  }

  a {
    color: $midnight;
  }

  &.doubles {
    height: 70px;

    .player {
      gap: 6px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      > div {
        padding-right: 12px;
        overflow: hidden;
        width: 100%;
      }

      .player-wrapper {
        overflow: hidden;

        p {
          font-size: 1rem;
        }
      }
    }

    .pdga,
    .rating {
      flex-direction: column;
      align-items: flex-end;
      gap: 6px;

      a,
      p {
        display: flex;
        align-items: center;
        height: 24px;
      }
    }
  }

  .position,
  .player,
  .show {
    display: flex;
    align-items: center;
  }

  .position {
    justify-content: center;
  }

  .show {
    padding-right: 12px;
    justify-content: flex-end;
  }

  .active {
    @include Gilroy-Bold;
  }

  .desktop {
    display: none;
  }

  .player {
    overflow: hidden;

    > div {
      overflow: hidden;
    }
  }

  .player-wrapper {
    cursor: pointer;
    display: flex;
    align-items: center;
    overflow: hidden;
    max-width: calc(100%);

    p {
      width: 100%;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      margin-left: 10px;
    }
  }
}

.placeholder-message {
  margin-top: 48px;
}

.pagination-wrapper {
  padding: 0 12px;
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 16px 0;
}

.desktop-nav {
  display: none;
}

.mobile-nav {
  margin: 0 12px 28px 12px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: stretch;

  .division-selector,
  .round-selector {
    margin: 0;
    height: 40px;
    background-position-x: calc(100% - 12px);
    border: 1px solid $midnight;
    border-radius: 6px;
    width: auto;
    padding-right: 40px;
    flex-grow: 1;
    flex-basis: 0;
  }

  .division-selector {
    flex-basis: 65%;
  }

  .round-selector {
    flex-shrink: 2;
    max-width: 100%;
  }
}

@media (min-width: 1200px) {
  .table-body {
    user-select: unset !important;
  }

  .quick-nav-info-container {
    display: none;
  }

  .waitinglist-rules {
    width: auto;
  }

  .player-table {
    padding: 0;

    &.combined {
      .table-header {
        grid-template-columns: 50px 1fr 100px 1fr 0.4fr 0.4fr 0.7fr;

        .division {
          display: flex;
        }
      }

      .player-row {
        grid-template-columns: 50px 1fr 100px 1fr 0.4fr 0.4fr 0.7fr;
        padding-right: 16px;

        .division {
          display: block;
          text-align: left;
        }
      }
    }

    &.teamname {
      .table-header,
      .player-row {
        .position {
          display: flex;
        }

        .player {
          padding-left: 0;
        }

        grid-template-columns: 50px auto 0.7fr;

        .club,
        .pdga,
        .rating {
          display: none;
        }
      }

      .table-header {
        padding-left: 0;
      }

      &.combined {
        .table-header,
        .player-row {
          grid-template-columns: 50px 1fr 100px 150px;
        }
      }
    }
  }

  .registered,
  .pdga,
  .rating {
    align-items: center;
    justify-content: flex-end;

    p {
      text-align: right;
    }

    a {
      color: $midnight;
    }
  }

  .table-header {
    padding: 0 16px 0 0;
    grid-template-columns: 50px 1fr 1fr 0.4fr 0.4fr 0.7fr;
    height: 48px;
    margin-bottom: 16px;

    .position {
      display: block;
    }

    .registered {
      cursor: pointer;

      p {
        &::after {
          content: 'Registered';
        }
      }
    }

    .nav-arrows {
      display: none;
    }

    .player,
    .club,
    .pdga,
    .rating {
      cursor: pointer;
      display: flex;
    }

    .club {
      cursor: default;
    }

    .player {
      cursor: pointer;
      display: flex;
      align-items: center;
    }

    > div.active {
      .desktop {
        margin-right: 6px;
        display: flex;
      }

      .mobile {
        display: none;
      }
    }
  }

  .player-row {
    height: 56px;
    padding-right: 16px;
    grid-template-columns: 50px 1fr 1fr 0.4fr 0.4fr 0.7fr;

    .registered {
      padding-right: 0;
    }

    &.doubles {
      height: 90px;

      .club,
      .pdga,
      .rating {
        display: flex;
        flex-direction: column;
        gap: 6px;

        p {
          display: flex;
          align-items: center;
          height: 32px;
        }
      }
    }

    .club {
      overflow: hidden;

      p {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }
    }

    .club,
    .pdga,
    .rating,
    .registered {
      display: flex;
    }

    .mobile {
      display: none;
    }

    .desktop {
      display: flex;
    }

    .show {
      @include Gilroy-Regular;
    }

    .active {
      @include Gilroy-Bold;

      p {
        @include Gilroy-Bold;
      }
    }
  }

  .mobile {
    display: none;
  }

  .desktop {
    display: block;
  }

  .placeholder-container {
    margin: 48px auto;
  }

  .mobile-nav {
    display: none;
  }

  .desktop-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 48px;

    .view-switch {
      width: auto;
    }

    .selector-wrapper {
      display: flex;
      align-items: center;

      &.inactive {
        p {
          color: $strom;
        }
      }

      p {
        font-size: 24px;
        margin: 0;
      }

      svg {
        margin-left: 12px;
      }
    }

    .round-select {
      margin-left: 32px;
      padding-left: 32px;
      border-left: 1px solid $fog;
    }
  }

  .players-summary {
    padding: 20px 32px;
    margin: 0 0 64px 0px;
    display: flex;
    justify-content: space-between;

    p {
      display: flex;
      gap: 5px;
      font-size: 18px;
    }

    strong {
      @include Gilroy-Bold;
    }

    svg {
      width: 20px;
      height: 20px;
    }

    > div {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    span {
      display: block;
    }
  }
}
</style>
